<!--
  ArtifiDesignerPrototype.vue -- GTM3 / Artifi Phase B risk-retirement spike.

  PROTOTYPE, NOT PRODUCTION. Standalone page that loads Artifi's interactive
  Designer into the mandatory <div id="artifi-editor"> container and drives
  the documented initialize / AddToCart lifecycle inside a ThreadFlow-origin
  page. Reachable from the SuperAdmin Prototypes section + by direct URL.

  Must be served from an Artifi-allowlisted host (e.g. tf-sandbox.coules.io) --
  Artifi's allowlist matches the Origin/Referer host, not source IP. localhost
  fails.

  Contract (2026-07-01, Amla official doc):
    docs/integrations/artifi/steps-to-integrate-artifi-components-in-ecommerce.md
-->
<template>
  <div class="artifi-proto" data-testid="artifi-designer-prototype">
    <header class="artifi-proto__head">
      <h1 class="artifi-proto__title">Artifi Designer -- Phase B prototype</h1>
      <p class="artifi-proto__sub">
        Loads <code>Artifi.headless.js</code> and initializes the Designer into
        the <code>#artifi-editor</code> canvas for a seeded SKU, per Amla's
        component-integration doc. Watch the browser console for lifecycle
        events.
      </p>
    </header>

    <div class="artifi-proto__body">
      <!-- LEFT: controls -->
      <section class="artifi-proto__controls" data-testid="artifi-proto-controls">
        <!-- Step 1: choose a logo (upload OR pick from the distributor's
             decoration repository, via the shared EditorArtworkPicker). -->
        <div class="artifi-proto__step">
          <h2 class="artifi-proto__step-title">1. Choose a logo</h2>
          <p class="artifi-proto__hint">
            Upload a new file or pick an existing decoration from the
            distributor's repository. Artwork is tenant-scoped, so choose the
            owning distributor first (SuperAdmin has no tenant of its own).
          </p>
          <label class="artifi-proto__field">
            <span>Distributor (artwork owner)</span>
            <select
              v-model="distributorId"
              data-testid="artifi-proto-distributor"
              :disabled="distributorsLoading"
            >
              <option value="">
                {{ distributorsLoading ? 'Loading...' : '(select a distributor)' }}
              </option>
              <option v-for="d in distributors" :key="d.id" :value="d.id">
                {{ d.name }} (#{{ d.id }})
              </option>
            </select>
          </label>
          <button
            class="artifi-proto__btn"
            :disabled="!distributorId"
            data-testid="artifi-proto-choose-artwork-btn"
            @click="artworkPickerOpen = true"
          >
            Choose or upload artwork
          </button>
          <p
            v-if="uploadError"
            class="artifi-proto__error"
            data-testid="artifi-proto-upload-error"
          >
            {{ uploadError }}
          </p>
          <div
            v-if="uploadedArtwork"
            class="artifi-proto__uploaded"
            data-testid="artifi-proto-uploaded"
          >
            <img
              v-if="uploadedThumb"
              :src="uploadedThumb"
              alt="chosen logo"
              class="artifi-proto__thumb"
            />
            <span>
              Artwork #{{ uploadedArtwork.id }}
              <template v-if="uploadedArtwork.artifi_unique_name">
                - Artifi: {{ uploadedArtwork.artifi_unique_name }}
              </template>
              <em v-else>- no Artifi UniqueName yet</em>
            </span>
          </div>
        </div>

        <EditorArtworkPicker
          v-if="artworkPickerOpen"
          v-model="artworkPickerOpen"
          :distributor-id="distributorId"
          title="Choose or upload a logo"
          @picked="onArtworkPicked"
        />

        <!-- Step 2: product/variant selection -->
        <div class="artifi-proto__step">
          <h2 class="artifi-proto__step-title">2. Product</h2>
          <label class="artifi-proto__field">
            <span>Master SKU (productCode)</span>
            <input
              v-model="productCode"
              type="text"
              data-testid="artifi-proto-product-code"
            />
          </label>
          <label class="artifi-proto__field">
            <span>Variant SKU (color; optional)</span>
            <select v-model="variantSku" data-testid="artifi-proto-variant">
              <option value="">(master -- no variant)</option>
              <option v-for="v in seedVariants" :key="v.sku" :value="v.sku">
                {{ v.label }}
              </option>
            </select>
          </label>
          <label class="artifi-proto__field">
            <span>View (decoratable location)</span>
            <select v-model="viewCode" data-testid="artifi-proto-view">
              <option v-for="v in seedViews" :key="v" :value="v">{{ v }}</option>
            </select>
          </label>
        </div>

        <!-- Step 3: launch -->
        <div class="artifi-proto__step">
          <h2 class="artifi-proto__step-title">3. Designer</h2>
          <button
            class="artifi-proto__btn artifi-proto__btn--primary"
            :disabled="designerStatus === 'loading-script' || designerStatus === 'initializing'"
            data-testid="artifi-proto-launch-btn"
            @click="onLaunch"
          >
            Launch Designer
          </button>
          <button
            class="artifi-proto__btn"
            :disabled="designerStatus !== 'ready'"
            data-testid="artifi-proto-addtocart-btn"
            @click="onAddToCart"
          >
            Finish (Add to Cart)
          </button>
          <p class="artifi-proto__status" data-testid="artifi-proto-status">
            Status: <strong>{{ designerStatus }}</strong>
          </p>
          <p
            v-if="designerError"
            class="artifi-proto__error"
            data-testid="artifi-proto-designer-error"
          >
            {{ designerError }}
          </p>
        </div>

        <!-- handoff payload -->
        <div
          v-if="lastPayload"
          class="artifi-proto__step"
          data-testid="artifi-proto-payload"
        >
          <h2 class="artifi-proto__step-title">Handoff payload</h2>
          <pre class="artifi-proto__payload">{{ prettyPayload }}</pre>
        </div>
      </section>

      <!-- RIGHT: Artifi mounts its UI here. The composable creates the
           mandatory <div id="artifi-editor"> container inside this element and
           the bundle renders the canvas into it. -->
      <section class="artifi-proto__stage">
        <div
          ref="mountRef"
          class="artifi-proto__mount"
          data-testid="artifi-designer-mount"
        >
          <p class="artifi-proto__placeholder">
            The Artifi Designer will mount here after "Launch Designer".
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import api from '@/services/api'
import {
  useArtifiDesigner,
  ARTIFI_USER_ID,
} from '@/composables/useArtifiDesigner'
import EditorArtworkPicker from '@/pages/distributor/store-editor/StoreProducts/Editor/EditorArtworkPicker.vue'

const FULLBLEED_BODY_CLASS = 'tf-product-editor-active'
const mountRef = ref(null)
// Decoratable views configured for SKU 18000 (seed-data-2026-05-13.md).
const viewCode = ref('Full Center')
const seedViews = ['Full Center', 'Left Chest', 'Right Chest', 'Center Back', 'Upper Back']

// Canonical seeded SKU (Gildan DryBlend Crewneck) + a sampling of variant
// SKUs from docs/integrations/artifi/seed-data-2026-05-13.md. Variant SKU
// encodes color; color is NOT a separate Designer parameter.
// Port & Co Core Cotton Tee (PC54). Artifi's GetLaunchConfigData keys off
// the numeric VARIANT sku (color), not the master -- launching with no
// variant sends sku=undefined -> 400. These PC54 variant SKUs were resolved
// from Artifi Website 445's GetProductAttributes + verified to launch
// (Response=Success, MasterSKU=PC54) live 2026-07-02.
const productCode = ref('PC54')
const variantSku = ref('2286161')
const seedVariants = [
  { sku: '2286161', label: 'White (2286161)' },
  { sku: '2286041', label: 'Navy (2286041)' },
  { sku: '2286121', label: 'Royal (2286121)' },
  { sku: '2286101', label: 'Red (2286101)' },
  { sku: '2286421', label: 'Ash (2286421)' },
]

const designer = useArtifiDesigner()

const {
  status: designerStatus,
  errorMessage: designerError,
  lastPayload,
  launch,
  addToCart,
} = designer

// ---- distributor picker (artwork is tenant-scoped; SA has no tenant) ----
const distributors = ref([])
const distributorsLoading = ref(false)
const distributorId = ref('')

// ---- chosen artwork (via the shared EditorArtworkPicker) ----
const artworkPickerOpen = ref(false)
const uploadError = ref(null)
const uploadedArtwork = ref(null)

const uploadedThumb = computed(
  () => uploadedArtwork.value?.thumbnail_url || uploadedArtwork.value?.image_url || null,
)

const prettyPayload = computed(() =>
  lastPayload.value ? JSON.stringify(lastPayload.value, null, 2) : '',
)

async function loadDistributors() {
  distributorsLoading.value = true
  try {
    // SAFRS distributors collection (SA-readable). The prototype is
    // SA-gated, so the SA picks whose tenant owns the chosen artwork.
    const res = await api.get('/distributors/')
    distributors.value = (res.data?.data || []).map((d) => ({
      id: d.id,
      name: d.attributes?.name || `Distributor ${d.id}`,
    }))
  } catch (err) {
    uploadError.value = `Could not load distributors: ${
      err?.message || err
    }`
  } finally {
    distributorsLoading.value = false
  }
}

// The EditorArtworkPicker emits both the upload-new and pick-existing cases.
// It surfaces artifiUniqueName when known; for an existing library artwork
// that hasn't been registered with Artifi yet, register it on demand via
// process_assets so the Designer preload has a UniqueName to place.
async function onArtworkPicked(payload) {
  uploadError.value = null
  const id = payload?.artworkFileId
  uploadedArtwork.value = {
    id,
    artifi_unique_name: payload?.artifiUniqueName || null,
    thumbnail_url: payload?.thumbnailUrl || null,
  }
  if (!payload?.artifiUniqueName && id) {
    try {
      const res = await api.post(`/artworks/${id}/process_assets`, {})
      const uname = res?.data?.artifi_unique_name
      if (uname) {
        uploadedArtwork.value = {
          ...uploadedArtwork.value,
          artifi_unique_name: uname,
          thumbnail_url:
            res.data.thumbnail_url || uploadedArtwork.value.thumbnail_url,
        }
      }
    } catch (err) {
      // Non-fatal: the artwork is chosen; it just isn't Artifi-registered
      // yet, so the Designer preload will have nothing to place.
      uploadError.value =
        'Chosen artwork is not registered with Artifi yet ' +
        `(${err?.message || err}).`
    }
  }
}

async function onLaunch() {
  // Artifi requires the numeric variant sku; without it GetLaunchConfigData
  // 400s. Guard here with a clear message rather than surfacing Artifi's
  // cryptic "Invalid SKU or product code".
  if (!variantSku.value) {
    designerError.value =
      'Select a variant SKU (color) before launching -- Artifi requires it.'
    return
  }
  try {
    await launch({
      mountEl: mountRef.value,
      productCode: productCode.value,
      sku: variantSku.value,
      viewCode: viewCode.value,
      // IMPORTANT: use the SAME Artifi user id the backend bridge uploads
      // under. The bridge uploads our logo to Artifi's UploadImage as Artifi
      // account "1" (ARTIFI_USER_ID; Website 445 accepts only "1"). Artifi
      // scopes uploaded photos per userId, so the Designer must launch as
      // "1" for the uploaded logo to be visible to it (My Art / preload).
      // Launching as tf-acct-<id> would hide the just-uploaded image.
      userId: ARTIFI_USER_ID,
      // Artifi UniqueName of the uploaded logo (from the backend bridge via
      // process_assets). Carried into the Designer so it can be preloaded
      // onto the canvas once the vendor-confirmed preload mechanism is known.
      artifiUniqueName: uploadedArtwork.value?.artifi_unique_name || null,
    })
  } catch {
    // error surfaced via designerError
  }
}

function onAddToCart() {
  try {
    addToCart()
  } catch (err) {
    designerError.value = err?.message || String(err)
  }
}

onMounted(() => {
  if (typeof document !== 'undefined' && document.body) {
    document.body.classList.add(FULLBLEED_BODY_CLASS)
  }
  loadDistributors()
})

onUnmounted(() => {
  if (typeof document !== 'undefined' && document.body) {
    document.body.classList.remove(FULLBLEED_BODY_CLASS)
  }
  // Tear down the composable's Artifi window listeners so a
  // mount->unmount->remount cycle doesn't leak dead listeners bound to
  // window (concurrency review, PR #1812).
  designer.dispose()
})
</script>

<style scoped>
.artifi-proto {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f5f6f8;
  color: #1a1a1a;
}
.artifi-proto__head {
  padding: 16px 24px;
  border-bottom: 1px solid #e2e4e8;
  background: #fff;
}
.artifi-proto__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}
.artifi-proto__sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: #5a5f6b;
}
.artifi-proto__body {
  display: flex;
  flex: 1 1 auto;
  gap: 16px;
  padding: 16px 24px;
  align-items: stretch;
}
.artifi-proto__controls {
  flex: 0 0 360px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.artifi-proto__step {
  background: #fff;
  border: 1px solid #e2e4e8;
  border-radius: 8px;
  padding: 14px 16px;
}
.artifi-proto__step-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
}
.artifi-proto__hint {
  margin: 0 0 8px;
  font-size: 12px;
  color: #6b7280;
}
.artifi-proto__field {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
}
.artifi-proto__field span {
  display: block;
  margin-bottom: 2px;
  color: #6b7280;
}
.artifi-proto__field input,
.artifi-proto__field select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #cbd0d8;
  border-radius: 6px;
  font-size: 13px;
}
.artifi-proto__btn {
  margin: 8px 8px 0 0;
  padding: 7px 14px;
  border: 1px solid #cbd0d8;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
}
.artifi-proto__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.artifi-proto__btn--primary {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
.artifi-proto__status {
  margin: 10px 0 0;
  font-size: 12px;
}
.artifi-proto__error {
  margin: 8px 0 0;
  font-size: 12px;
  color: #b91c1c;
}
.artifi-proto__uploaded {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 12px;
}
.artifi-proto__thumb {
  width: 40px;
  height: 40px;
  object-fit: contain;
  border: 1px solid #e2e4e8;
  border-radius: 4px;
  background: #fff;
}
.artifi-proto__payload {
  max-height: 220px;
  overflow: auto;
  font-size: 11px;
  background: #0f172a;
  color: #e2e8f0;
  padding: 10px;
  border-radius: 6px;
}
.artifi-proto__stage {
  flex: 1 1 auto;
  background: #fff;
  border: 1px solid #e2e4e8;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
}
.artifi-proto__mount {
  flex: 1 1 auto;
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.artifi-proto__placeholder {
  color: var(--tf-fg-4);
  font-size: 14px;
}
</style>
